/**
 * GraphQL data access for the search filter bar dropdowns.
 *
 * All three filter dropdowns (Namespace, String Group, CX Family) fetch their
 * full option list up front by walking `nextToken` pages, then filter locally.
 * That loop — page cap, dev-mode truncation warning, and stale-request abort —
 * is identical for every dropdown, so it lives once in `fetchAllPages` and each
 * query gets a thin wrapper below.
 *
 * `fetchAllPages` is a plain async function, not a hook: the String Group flow
 * calls it once per namespace inside a per-namespace cache loop.
 */
import type {
  ListCxFamiliesQuery,
  ListCxFamiliesQueryVariables,
  ListNamespacesQuery,
  ListNamespacesQueryVariables,
  ListStringGroupsQuery,
  ListStringGroupsQueryVariables,
} from 'src/generated/graphql';
import { MAX_PAGES, MAX_PAGE_SIZE } from './constants';

/** Item shapes returned by the three list queries. */
export type NamespaceItem =
  ListNamespacesQuery['listNamespaces']['namespaces'][number];
export type StringGroupItem =
  ListStringGroupsQuery['listStringGroups']['stringGroups'][number];
export type CxFamilyItem =
  ListCxFamiliesQuery['listCxFamilies']['cxFamilies'][number];

/**
 * Minimal structural type for an Apollo `useXxxLazyQuery` execute function.
 * Declared structurally so this module does not depend on Apollo's hook types
 * and so tests can supply a plain jest mock.
 */
export type ExecuteQuery<TData, TVariables> = (options: {
  variables: TVariables;
}) => Promise<{ data?: TData | null }>;

/** One page of results pulled out of a paginated GraphQL response. */
export interface Page<TItem> {
  readonly items: readonly TItem[];
  readonly nextToken?: string | null;
}

export interface FetchAllPagesConfig<TItem, TData> {
  /**
   * Fetches a single page. Receives the previous page's nextToken, or
   * `undefined` for the first page.
   */
  readonly fetchPage: (
    nextToken: string | undefined,
  ) => Promise<{ data?: TData | null }>;
  /** Pulls the items and nextToken out of a page's data. */
  readonly extractPage: (data: TData) => Page<TItem>;
}

export interface FetchAllPagesOptions {
  /**
   * Returns true when the caller's request has been superseded. Checked before
   * and after every await so a stale fetch neither keeps paginating nor lets
   * its result reach state.
   */
  readonly isStale: () => boolean;
  /** Page cap guarding against unbounded pagination. Defaults to MAX_PAGES. */
  readonly maxPages?: number;
  /** Query name used in the dev-mode truncation warning, e.g. 'listNamespaces'. */
  readonly warnLabel: string;
  /** Optional scope appended to the warning, e.g. 'for namespace "ns-a"'. */
  readonly warnScope?: string;
  /** Plural noun for the warning, e.g. 'namespaces'. */
  readonly itemLabel?: string;
}

export interface FetchAllPagesResult<TItem> {
  readonly items: TItem[];
  /**
   * True when pagination stopped at the page cap while a nextToken was still
   * pending — the item list is a prefix of the full result set.
   */
  readonly truncated: boolean;
  /**
   * True when `isStale()` returned true mid-flight. Callers MUST NOT apply an
   * aborted result to state.
   */
  readonly aborted: boolean;
}

const abortedResult = <TItem>(): FetchAllPagesResult<TItem> => ({
  items: [],
  truncated: false,
  aborted: true,
});

/**
 * Walks every `nextToken` page of a paginated query and accumulates the items.
 *
 * Stops early when `isStale()` reports the request has been superseded, or when
 * `maxPages` is reached (reported back as `truncated`, and warned about in
 * non-production builds).
 */
export async function fetchAllPages<TItem, TData>(
  { fetchPage, extractPage }: FetchAllPagesConfig<TItem, TData>,
  {
    isStale,
    maxPages = MAX_PAGES,
    warnLabel,
    warnScope,
    itemLabel = 'items',
  }: FetchAllPagesOptions,
): Promise<FetchAllPagesResult<TItem>> {
  const items: TItem[] = [];
  let nextToken: string | null | undefined = undefined;
  let pageCount = 0;

  do {
    if (isStale()) return abortedResult<TItem>();
    const result = await fetchPage(nextToken || undefined);
    if (isStale()) return abortedResult<TItem>();

    if (result.data) {
      const page = extractPage(result.data);
      items.push(...page.items);
      nextToken = page.nextToken;
    } else {
      nextToken = null;
    }
    pageCount++;
  } while (nextToken && pageCount < maxPages);

  const truncated = Boolean(nextToken) && pageCount >= maxPages;
  if (truncated && process.env.NODE_ENV !== 'production') {
    const scope = warnScope ? ` ${warnScope}` : '';
    console.warn(
      `${warnLabel} pagination capped at ${maxPages} pages${scope}. Some ${itemLabel} may not be shown.`,
    );
  }

  return { items, truncated, aborted: false };
}

/** Fetches every namespace the user can access. */
export const fetchAllNamespaces = (
  execute: ExecuteQuery<ListNamespacesQuery, ListNamespacesQueryVariables>,
  isStale: () => boolean,
): Promise<FetchAllPagesResult<NamespaceItem>> =>
  fetchAllPages(
    {
      fetchPage: (nextToken) =>
        execute({ variables: { pageSize: MAX_PAGE_SIZE, nextToken } }),
      extractPage: ({ listNamespaces }) => ({
        items: listNamespaces.namespaces,
        nextToken: listNamespaces.nextToken,
      }),
    },
    { isStale, warnLabel: 'listNamespaces', itemLabel: 'namespaces' },
  );

/** Fetches every string group in a single namespace. */
export const fetchAllStringGroupsForNamespace = (
  execute: ExecuteQuery<ListStringGroupsQuery, ListStringGroupsQueryVariables>,
  namespaceId: string,
  isStale: () => boolean,
): Promise<FetchAllPagesResult<StringGroupItem>> =>
  fetchAllPages(
    {
      fetchPage: (nextToken) =>
        execute({
          variables: { namespaceId, pageSize: MAX_PAGE_SIZE, nextToken },
        }),
      extractPage: ({ listStringGroups }) => ({
        items: listStringGroups.stringGroups,
        nextToken: listStringGroups.nextToken,
      }),
    },
    {
      isStale,
      warnLabel: 'listStringGroups',
      warnScope: `for namespace "${namespaceId}"`,
      itemLabel: 'groups',
    },
  );

/** Fetches every CX family (with its cxIds) the user can access. */
export const fetchAllCxFamilies = (
  execute: ExecuteQuery<ListCxFamiliesQuery, ListCxFamiliesQueryVariables>,
  isStale: () => boolean,
): Promise<FetchAllPagesResult<CxFamilyItem>> =>
  fetchAllPages(
    {
      fetchPage: (nextToken) =>
        execute({ variables: { pageSize: MAX_PAGE_SIZE, nextToken } }),
      extractPage: ({ listCxFamilies }) => ({
        items: listCxFamilies.cxFamilies,
        nextToken: listCxFamilies.nextToken,
      }),
    },
    { isStale, warnLabel: 'listCxFamilies', itemLabel: 'families' },
  );
